/**
 * PaymentsModal - Consistent payment gate modal for premium features
 * Addresses S-1003 AC1, AC2, AC4: Modal consistency and state management
 * S-1006: Integrated with ModalManager for race condition prevention
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Sparkles, Target, Zap } from 'lucide-react';
import { useModalManager, useModalFocus } from '@/hooks/useModalManager';

interface PaymentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  entryPoint?: string;
}

export function PaymentsModal({ 
  isOpen, 
  onClose, 
  feature = "Premium Feature",
  entryPoint = "unknown"
}: PaymentsModalProps) {
  
  // S-1006: Integrate with ModalManager for race condition prevention
  const modalId = `payments-${feature}-${entryPoint}`;
  const modalManager = useModalManager({
    modalId,
    type: 'payments',
    priority: 10, // High priority for payment modals
    canStack: false, // Payment modals should not stack
    blockingLevel: 'same-type', // Block other payment modals
    onClose,
    metadata: { feature, entryPoint }
  });

  const { modalRef } = useModalFocus(modalId, modalManager.isOpen);

  // Sync external isOpen prop with ModalManager
  useEffect(() => {
    if (isOpen && !modalManager.isOpen) {
      modalManager.open();
    } else if (!isOpen && modalManager.isOpen) {
      modalManager.close();
    }
  }, [isOpen, modalManager]);

  const handleUpgrade = () => {
    // TODO: Implement actual payment flow when payments are enabled
    console.log(`[PaymentsModal] Upgrade clicked for ${feature} from ${entryPoint}`);
    // For now, just close the modal since payments aren't implemented
    modalManager.close();
  };

  const handleClose = () => {
    modalManager.close();
  };

  return (
    <Dialog open={modalManager.isOpen} onOpenChange={handleClose}>
      <DialogContent 
        ref={modalRef}
        className="sm:max-w-md"
        data-testid="payments-modal"
        style={{ zIndex: modalManager.zIndex }}
        // S-2203: Improved focus trapping and backdrop handling
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // S-2203: Ensure proper focus trapping
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleClose();
        }}
      >
        <DialogHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          
          <DialogTitle className="text-xl font-semibold">
            Upgrade to Premium
          </DialogTitle>
          
          <DialogDescription className="text-base">
            <span className="font-medium text-foreground">{feature}</span> is available with No Doubles Premium.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <span>Advanced AI swing analysis</span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <span>Real-time voice coaching</span>
            </div>
            
            <div className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
              <span>Unlimited analysis runs</span>
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <Badge variant="secondary" className="mb-2">
              Coming Soon
            </Badge>
            <p className="text-xs text-muted-foreground">
              Premium subscriptions will be available soon. 
              All features are currently free during beta.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5 text-xs text-amber-900">
            <p>
              Your subscription will automatically renew at the end of each billing period unless
              you cancel before the renewal date.
            </p>
            <p>
              You can cancel anytime in your account settings or through App Store / Play Store
              settings.
            </p>
            <Link
              to="/terms/subscription"
              target="_blank"
              className="inline-block text-emerald-700 hover:underline font-medium"
            >
              View Subscription Terms
            </Link>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleClose}
            data-testid="payments-modal-dismiss"
          >
            Maybe Later
          </Button>
          <Button 
            onClick={handleUpgrade}
            disabled
            data-testid="payments-modal-upgrade"
            title="Premium subscriptions coming soon"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PaymentsModal;
